import React, { useEffect, useRef } from 'react';

import styled from '@emotion/styled';

import { StorageCheckoutStepProps } from '@root/components/checkout/types';

import { hasChallengerPickupAddress } from '../completion';
import {
  Card,
  HelpText,
  Question,
  SectionStack,
  SectionTitle,
} from '../shared';
import { CHALLENGER as T } from '../tokens';
import { ChallengerAddressFields } from './address_fields';
import { ChallengerAppointmentCalendar } from './appointment_calendar';

const AddressCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${T.space[24]};
`;

/** Figma 1:3247 — question + schedule description, 4px gap. */
const AddressHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[4]};
`;

type Props = {
  stepProps: StorageCheckoutStepProps;
};

/**
 * Figma Desktop 03 / mobile pickup block:
 * 1) Address card (street + apt)
 * 2) Calendar card (“When should we arrive?”)
 * 3) Arrival card (“What time works best for you?”) — always visible
 *
 * When pickup ZIP changes or the street address is cleared, clear the
 * selected appointment so a stale day is not highlighted.
 */
export const PickupSection: React.FC<Props> = ({ stepProps }) => {
  const {
    onChange,
    values: { address, zip },
  } = stepProps;

  const previousZipRef = useRef<string | undefined>(undefined);
  const previousAddressReadyRef = useRef<boolean | undefined>(undefined);
  const addressReady = hasChallengerPickupAddress(address);

  useEffect(() => {
    if (!zip) return;

    const previousZip = previousZipRef.current;
    if (previousZip !== undefined && previousZip !== zip) {
      onChange('datePreferred', undefined);
      onChange('dateScheduled', undefined);
    }
    previousZipRef.current = zip;
  }, [onChange, zip]);

  useEffect(() => {
    const previousReady = previousAddressReadyRef.current;
    if (previousReady === true && !addressReady) {
      onChange('datePreferred', undefined);
      onChange('dateScheduled', undefined);
    }
    previousAddressReadyRef.current = addressReady;
  }, [addressReady, onChange]);

  return (
    <SectionStack>
      <SectionTitle>Pickup details</SectionTitle>

      <AddressCard>
        <AddressHeader>
          <Question>Where are we picking up your items?</Question>
          {!addressReady && (
            <HelpText>
              Enter a complete address (including city and ZIP) to see pickup
              availability.
            </HelpText>
          )}
        </AddressHeader>
        <ChallengerAddressFields {...stepProps} />
      </AddressCard>

      <ChallengerAppointmentCalendar
        onChange={stepProps.onChange}
        enabled={stepProps.enabled}
        values={stepProps.values}
      />
    </SectionStack>
  );
};
